import { ApolloClient, createHttpLink, DocumentNode, gql, InMemoryCache, TypedDocumentNode } from '@apollo/client';
import {
    ContactInput,
    CreativeCredit,
    CreativeCreditInput,
    InquiryInput,
    LineItem,
    LineItemInput,
    Mutation,
    MutationBulkSaveLineItemsArgs,
    MutationDeleteLineItemArgs,
    MutationSaveShortlistArgs,
    Query,
    QueryDraftShortlistAndLineItemsArgs,
    Reference,
    Shortlist,
    ShortlistInput
} from 'sr-types/lib/shortlist/v1/graphql';
import { authLink } from '../common/auth/authLink';
import { AllPermissions } from '../common/auth/permissions';
import {
    ContactInfoFragment,
    CreativeCreditFragment,
    DateRangeFragment,
    ReferenceFragment,
    RepresentativesFragment
} from '../common/list/fragments';
import { LineItemWithKeyCredits } from './shortlistLineItem';
import { LineItemExtended, LineItemRows } from './ShortlistLineItems';
import { stripProfileImageFromContacts } from './utils/representativeNormalizationUtils';
import { InquiryStatusEnum } from './types';


export const client = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/shortlist/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export type ReferenceWithLineItemId = Reference & { shortlistLineItemId: string };

export const saveQuery: TypedDocumentNode<Pick<Mutation, 'saveShortlist'>, MutationSaveShortlistArgs> = gql`
    mutation SaveShortlist($input: ShortlistInput!) {
        saveShortlist(input: $input) {
            id
            reference {
                ...Reference
            }
            errors {
                field
                message
            }
        }
    }
    ${ReferenceFragment}
`;

export const detailsQuery: DocumentNode = gql`
    query Shortlist($shortlistId: ID!) {
        shortlist(id: $shortlistId) {
            identity {
                id
            }
            name
            summary {
                about
                headline
            }
            context {
                ...Reference
            }
            type
            categories
            roles
            sequence {
                id
                type
                label
                uriPrefix
            }
            lineItems(page: { from: 0, size: 100 }) {
                name
                identity {
                    id
                }
                referenceUrl {
                    path
                    tldomain
                    subdomain
                }
                itemReference {
                    ...Reference
                }
                summary {
                    about
                    headline
                }
                representatives {
                    name {
                        firstName
                        lastName
                    }
                    personReference {
                        ...Reference
                    }
                    organization
                    organizationReference {
                        ...Reference
                    }
                    role
                    contactInfo {
                        ...ContactInfo
                    }
                    isPrimary
                }
                itemStatus
                inquiry {
                    productionName
                    role
                    synopsis
                    message
                    inquiryStatus
                    assignmentPeriod {
                        ...DateRange
                    }
                    proposedDate {
                        ...DateRange
                    }
                    locations {
                        label
                        primary
                    }
                }
                defaultNoteReference {
                    id
                }
                isItemSharedInDirectory
                keyCredits {
                    ...CreativeCredit
                }
                keyRoles
                security {
                    allowedActions
                }
            }
            sharedWithAccounts {
                actor {
                    id
                    name
                    email
                }
                action
            }
            security {
                allowedActions
            }
            ownerOrganizationId
            ownerOrganizationName
        }
    }
    ${DateRangeFragment}
    ${ReferenceFragment}
    ${CreativeCreditFragment}
    ${ContactInfoFragment}
`;

export const deleteQuery: DocumentNode = gql`
    mutation DeleteShortlist($id: ID!) {
        deleteShortlist(id: $id) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addItemToShortlist: DocumentNode = gql`
    mutation AddItemToShortlist($id: ID!, $input: ItemInput!) {
        addItemToShortlist(id: $id, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveLineItem: DocumentNode = gql`
    mutation SaveLineItem($shortlistId: ID!, $input: LineItemInput!) {
        saveLineItem(shortlistId: $shortlistId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveLineItems: DocumentNode = gql`
    mutation SaveLineItems($shortlistId: ID!, $input: [LineItemInput]!) {
        saveLineItems(shortlistId: $shortlistId, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveLineItemWithNote: DocumentNode = gql`
    mutation SaveLineItemWithNote($shortlistId: ID!, $input: LineItemInput!, $note: NoteInput) {
        saveLineItemWithNote(shortlistId: $shortlistId, input: $input, note: $note) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const bulkSaveLineItems: TypedDocumentNode<
    Pick<Mutation, 'bulkSaveLineItems'>,
    MutationBulkSaveLineItemsArgs
> = gql`
    mutation BulkSaveLineItems($input: BulkSaveLineItemRequest!) {
        bulkSaveLineItems(input: $input) {
            createdCount
            updatedCount
            unprocessedCount
            results {
                id
                errors {
                    field
                    message
                }
                reference {
                    ...Reference
                }
            }
            logFile {
                id
                uriPrefix
            }
        }
    }
    ${ReferenceFragment}
`;

export const deleteLineItemFromShortlist: TypedDocumentNode<
    Pick<Mutation, 'deleteLineItem'>,
    MutationDeleteLineItemArgs
> = gql`
    mutation deleteLineItem($shortlistId: ID!, $lineItemId: ID!) {
        deleteLineItem(shortlistId: $shortlistId, lineItemId: $lineItemId) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const draftShortlistAndLineItems: TypedDocumentNode<
    Pick<Query, 'draftShortlistAndLineItems'>,
    QueryDraftShortlistAndLineItemsArgs
> = gql`
    query draftShortlistAndLineItems($lineItemIds: [ID!]!, $resourceLabel: String!, $shortlistId: ID!) {
        draftShortlistAndLineItems(
            lineItemIds: $lineItemIds
            resourceLabel: $resourceLabel
            shortlistId: $shortlistId
        ) {
            draftShortlist {
                name
                type
                categories
                roles
                identity {
                    id
                    preSave
                }
                summary {
                    headline
                    about
                }
            }
            draftLineItems {
                name
                itemStatus
                identity {
                    id
                    preSave
                }
                itemReference {
                    ...Reference
                }
                summary {
                    headline
                    about
                }
                referenceUrl {
                    path
                    tldomain
                    subdomain
                }
                keyCredits {
                    ...CreativeCredit
                }
            }
        }
    }
    ${ReferenceFragment}
    ${CreativeCreditFragment}
`;

export const inquiryPreviewQuery: DocumentNode = gql`
    query InquiryPreview($inquiryInput: InquiryPreviewInput) {
        inquiryPreview(inquiryInput: $inquiryInput) {
            inquiryMessage {
                content
                senderInfo {
                    address
                    name
                }
            }
            inquiryFor
        }
    }
`;

export const generateSummaryQuery: DocumentNode = gql`
    query GenerateSummary($shortlistLineItemId: ID!, $url: String!, $size: String!) {
        generateSummary(shortlistLineItemId: $shortlistLineItemId, url: $url, size: $size)
    }
`;

export const shortlistForCrewQuery: DocumentNode = gql`
    query ShortlistForCrew(
        $shortlistType: String!
        $role: String!
        $userRequirements: String!
        $numOfCandidates: Int!
    ) {
        shortlistForCrew(
            shortlistType: $shortlistType
            role: $role
            userRequirements: $userRequirements
            numOfCandidates: $numOfCandidates
        ) {
            shortlistName
            items {
                firstName
                lastName
                email
                referenceUrl
                about
            }
        }
    }
`;

export const extractLineItemsFromEmailQuery: DocumentNode = gql`
    query extractLineItemsFromEmail($storageFileId: String!) {
        extractLineItemsFromEmail(storageFileId: $storageFileId) {
            identity {
                id
            }
            name
            itemReference {
                ...Reference
            }
            referenceUrl {
                path
                tldomain
                subdomain
            }
            summary {
                about
                headline
            }
            keyCredits {
                ...CreativeCredit
            }
            keyRoles
            itemStatus
            representatives {
                ...Representatives
            }
        }
    }
    ${ReferenceFragment}
    ${CreativeCreditFragment}
    ${RepresentativesFragment}
`;

export const EMPTY_SHORTLIST: ShortlistInput = {
    identity: { id: '' },
    name: '',
    roles: [],
    categories: [],
    summary: { about: '', headline: '' },
    type: '',
    sequence: undefined
};

export const inquiriesInputToShortlist = (
    inquiriesInput: InquiryInput[],
    prevShortlistLineItemsState: LineItemExtended[]
): LineItem[] => {
    const lineItems = [...prevShortlistLineItemsState];

    const updatedLineItems: LineItem[] = lineItems.map((lineItem) => {
        const found = inquiriesInput.find((item) => item.shortlistLineItemId === lineItem.identity.id);

        if (found) {
            return {
                ...lineItem,
                inquiry: { ...found.inquiryPayload, inquiryStatus: InquiryStatusEnum.requestSent }
            };
        }
        return lineItem;
    });

    return updatedLineItems;
};

export const deleteItemFromState = (lineItemId: string, prevShortlistState: Shortlist): Shortlist => {
    const { lineItems } = prevShortlistState;
    lineItems.splice(
        lineItems.findIndex((i) => i.identity.id === lineItemId),
        1
    );
    return {
        ...prevShortlistState,
        lineItems
    };
};

export const deleteLineItemFromList = (
    lineItemId: string,
    prevLineItemsState: LineItemExtended[]
): LineItemExtended[] => {
    const updated = [...prevLineItemsState];
    const index = updated.findIndex((i) => i.identity.id === lineItemId);
    if (index === -1) return prevLineItemsState;
    updated.splice(index, 1);
    return updated;
};

export const DEFAULT_ALLOWED_ACTIONS = [
    AllPermissions.Comment,
    AllPermissions.Edit,
    AllPermissions.Delete,
    AllPermissions.Read,
    AllPermissions.Share
];

export const lineItemInputToShortlist = (
    lineItemEditState: LineItemInput,
    lineItemId: string,
    prevShortlistState: Shortlist,
    note: string
): Shortlist => {
    const noteExists = Boolean(note);
    const updatedLineItems = [...prevShortlistState.lineItems];

    const found = updatedLineItems.findIndex((item) => item.identity.id === lineItemId);
    const newItem = {
        ...lineItemEditState,
        identity: { id: lineItemId },
        noteExists,
        security: { allowedActions: DEFAULT_ALLOWED_ACTIONS }
    };

    if (found !== -1) {
        updatedLineItems[found] = {
            ...updatedLineItems[found],
            ...newItem,
            security: updatedLineItems[found]?.security || newItem.security
        } as LineItemRows;
    } else {
        updatedLineItems.push(newItem as LineItemRows);
    }

    return { ...prevShortlistState, lineItems: updatedLineItems };
};

export const lineItemInputToShortlistLineItems = (
    lineItemEditState: LineItemInput,
    lineItemId: string,
    prevLineItemsState: LineItemExtended[],
    note: string,
    resourceShortList: Shortlist
) => {
    const noteExists = Boolean(note);
    const shortlistName = resourceShortList?.name ?? resourceShortList?.['label'] ?? '';

    const updated = [...prevLineItemsState];
    const found = prevLineItemsState.findIndex((item) => item.identity.id === lineItemId);

    const newItem = {
        ...lineItemEditState,
        identity: { id: lineItemId },
        noteExists,
        shortlist: shortlistName,
        resourceShortlistName: resourceShortList,
        security: { allowedActions: DEFAULT_ALLOWED_ACTIONS }
    };

    if (found !== -1) {
        updated[found] = {
            ...updated[found],
            ...newItem,
            security: updated[found]?.security || newItem.security
        } as LineItemExtended;
    } else {
        updated.push(newItem as LineItemExtended);
    }
    return updated;
};

export const getLineItemReference = (id: string): Reference => {
    return {
        deleted: false,
        eventId: '',
        id,
        type: 'ShortlistLineItem',
        uriPrefix: 'lineitems'
    };
};

const stripTypename = <T extends object | undefined | null>(value: T): T => {
    if (!value || typeof value !== 'object') return value;
    const { __typename, ...rest } = value as Record<string, unknown>;
    return rest as T;
};

/** Maps line-item key credits from GraphQL results to SaveLineItem input (strips Apollo `__typename`). */
export const toCreativeCreditInput = (credit: CreativeCredit): CreativeCreditInput => {
    if (!credit) return credit as unknown as CreativeCreditInput;
    const { dateRange, location, ...rest } = credit;
    const start = dateRange?.start?.trim();
    const end = dateRange?.end?.trim();
    return {
        showName: rest.showName ?? '',
        showType: rest.showType ?? '',
        showReference: rest.showReference ? stripTypename(rest.showReference) : undefined,
        department: rest.department ?? '',
        professionalRoles: rest.professionalRoles ?? [],
        keyCredit: rest.keyCredit ?? false,
        dateRange: start && end ? { start, end } : undefined,
        location: credit.location
            ? (stripTypename(credit.location) as unknown as CreativeCreditInput['location'])
            : undefined
    };
};

export const shortlistLineitemToInput = (shortlistLineitem: LineItem): LineItemInput => {
    return {
        defaultNoteReference: shortlistLineitem?.defaultNoteReference,
        identity: shortlistLineitem?.identity,
        itemReference: shortlistLineitem?.itemReference,
        itemStatus: shortlistLineitem?.itemStatus,
        name: shortlistLineitem?.name,
        referenceUrl: shortlistLineitem?.referenceUrl,
        summary: shortlistLineitem?.summary,
        representatives: stripProfileImageFromContacts(shortlistLineitem?.representatives as ContactInput[]),
        keyCredits: (shortlistLineitem?.keyCredits ?? []).filter(Boolean).map(toCreativeCreditInput),
        keyRoles: shortlistLineitem?.keyRoles ?? []
    };
};

/** Compares key-credit lists for grid save (order-sensitive). */
export const areKeyCreditsEqual = (
    left: LineItem['keyCredits'] | undefined,
    right: LineItem['keyCredits'] | undefined
): boolean => JSON.stringify(left ?? []) === JSON.stringify(right ?? []);

/** Updates `keyCredits` on one line item in local shortlist state (multi- or single-shortlist lists). */
export const patchLineItemKeyCredits = <T extends LineItemWithKeyCredits>(
    lineItems: T[],
    lineItemId: string,
    keyCredits: CreativeCredit[]
): T[] => lineItems.map((item) => (item.identity?.id === lineItemId ? { ...item, keyCredits } : item));

/**
 * Creates form listeners for representative editor that validates required fields.
 * Enables save button only when both personReference and role are provided.
 */
export const createRepresentativeEditorListeners = (setDisableSaveButton: (disabled: boolean) => void) => [
    {
        prop: ['personReference', 'role'],
        func: (_name: string, _newValue: unknown, newState: Record<string, unknown>) => {
            const hasPersonReference = (newState?.personReference as { id?: string })?.id;
            const hasRole = newState?.role;
            setDisableSaveButton(!hasPersonReference || !hasRole);
        }
    }
];
